import {
  FirebaseApp,
  FirebaseOptions,
  getApps,
  initializeApp
} from "firebase/app"
import {
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken
} from "firebase/auth"
import { getDatabase, onValue, ref } from "firebase/database"
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react"
import { getNodeEnvValue } from "../env/env"

export type FirebaseClientConfig = {
  url?: string
  client_api_key?: string
}

const DEFAULT_APP_NAME = "[DEFAULT]"

let app: FirebaseApp | undefined

export type InitializeFirebaseResult = {
  app: FirebaseApp | undefined
  /** True when reusing the default app (skip sign-in to avoid permission_denied elsewhere). */
  isExistingDefault: boolean
}

export const initializeFirebase = (
  firebaseConfig: FirebaseClientConfig | null | undefined,
  appName: string = DEFAULT_APP_NAME
): InitializeFirebaseResult => {
  if (!firebaseConfig?.url || !firebaseConfig?.client_api_key) {
    if (getNodeEnvValue() !== "test") {
      console.warn("Could not initialize Firebase: no Firebase config provided")
    }
    return { app: undefined, isExistingDefault: false }
  }

  const apps = getApps()
  const existing = apps.find((a) => a.name === appName) ?? null
  if (existing) {
    app = existing
    // Only the unnamed default app is shared across providers in one window.
    // Per-user named apps re-auth when reused so token changes take effect.
    return {
      app,
      isExistingDefault: appName === DEFAULT_APP_NAME
    }
  }

  const options: FirebaseOptions = {
    apiKey: firebaseConfig.client_api_key,
    databaseURL: firebaseConfig.url
  }
  app =
    appName === DEFAULT_APP_NAME
      ? initializeApp(options)
      : initializeApp(options, appName)
  return { app, isExistingDefault: false }
}

export function getFirebaseDb(): ReturnType<typeof getDatabase> {
  if (!app) throw new Error("Firebase app not initialized")
  return getDatabase(app)
}

type FirebaseContextValue = {
  app: FirebaseApp | undefined
  isAuthenticated: boolean
}

const FirebaseContext = createContext<FirebaseContextValue>({
  app: undefined,
  isAuthenticated: false
})

export type FirebaseProviderProps = {
  children: ReactNode
  firebaseConfig: FirebaseClientConfig | null | undefined
  firebaseAuthToken: string | null | undefined
  /** Isolate Auth persistence (e.g. per Pathwright user on a shared origin). */
  appName?: string
}

export const FirebaseProvider = ({
  children,
  firebaseConfig,
  firebaseAuthToken,
  appName
}: FirebaseProviderProps) => {
  const { app: appVal, isAuthenticated } = _useFB(
    firebaseConfig,
    firebaseAuthToken,
    appName
  )
  return (
    <FirebaseContext.Provider value={{ app: appVal, isAuthenticated }}>
      {children}
    </FirebaseContext.Provider>
  )
}

function _useFB(
  firebaseConfig: FirebaseClientConfig | null | undefined,
  firebaseAuthToken: string | null | undefined,
  appName: string = DEFAULT_APP_NAME
) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const { app, isExistingDefault } = useMemo(
    () => initializeFirebase(firebaseConfig, appName),
    [firebaseConfig?.url, firebaseConfig?.client_api_key, appName]
  )

  useEffect(() => {
    if (!app) {
      setIsAuthenticated(false)
      return
    }

    const auth = getAuth(app)

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user)
    })

    // When reusing the default app, another provider already signed in. Skip sign-in
    // or we trigger permission_denied for other listeners (e.g. /path/.../analytics).
    if (isExistingDefault) {
      return () => unsubscribeAuth()
    }

    const handleAuth = async () => {
      setIsAuthenticated(false)

      try {
        if (auth.currentUser) {
          await auth.signOut()
        }

        if (firebaseAuthToken) {
          await signInWithCustomToken(auth, firebaseAuthToken)
        }
      } catch (error: unknown) {
        const code =
          error &&
          typeof error === "object" &&
          "code" in error &&
          String((error as { code?: string }).code)
        if (
          code === "auth/configuration-not-found" ||
          (typeof code === "string" && code.includes("configuration"))
        ) {
          console.error(
            "Firebase auth/configuration-not-found: enable Authentication in Firebase Console (Build → Authentication → Get started) and ensure the Web API key is allowed for Auth (Google Cloud Console → Credentials → your API key → API restrictions)."
          )
        }
        console.error("Error during Firebase authentication:", error)
      }
    }

    handleAuth()
    return () => unsubscribeAuth()
  }, [app, firebaseAuthToken, isExistingDefault])

  return { app, isAuthenticated }
}

export const useFirebaseContext = () => useContext(FirebaseContext)

export function useFirebaseValue<T = unknown>(path: string): readonly [T | undefined, ReturnType<typeof ref> | null] {
  const [value, setValue] = useState<T | undefined>()
  const { app, isAuthenticated } = useFirebaseContext()

  const dbRef = useMemo(() => {
    if (!app || !isAuthenticated || !path) {
      return null
    }

    const db = getFirebaseDb()
    return ref(db, path)
  }, [app, path, isAuthenticated])

  useEffect(() => {
    if (dbRef) {
      return onValue(
        dbRef,
        (snap) => {
          setValue(snap.val() as T)
        },
        (error) => {
          console.log("got error: ", error)
        }
      )
    } else {
      setValue(undefined)
    }
  }, [dbRef])

  return [value, dbRef] as const
}
